import { z } from "zod";

// Transport types supported by the MCP protocol.
// "streamable_http" is the current spec (2025-06-18+), "sse_legacy" is the deprecated
// HTTP+SSE transport, and "stdio" is reserved for local/sandboxed servers (frontend
// hides stdio in Phase 1 but the schema permits it for forward-compatibility).
export const TransportSchema = z.enum(["streamable_http", "sse_legacy", "stdio"]);
export type Transport = z.infer<typeof TransportSchema>;

export const CustomHeaderSchema = z.object({
  name: z.string().trim().min(1, "Header name is required"),
  value: z.string(),
  is_secret: z.boolean().default(true),
});
export type CustomHeader = z.infer<typeof CustomHeaderSchema>;

// Icon entry (MCP 2025-11-25) — mirrors the MCPIcon TS shape in mcp-service.ts.
// Kept narrow (only `src` is required) so a server that sends a minimal icon
// list doesn't get dropped on decode.
export const IconSchema = z.object({
  src: z.string(),
  mime_type: z.string().optional(),
  sizes: z.array(z.string()).optional(),
});
export type Icon = z.infer<typeof IconSchema>;

// ServerInfo (MCP 2025-11-25) — adds `description`, `website_url`, `icons` on
// top of the initialize.result.serverInfo {name, version, title} shape so the
// manage page header can render the new fields.
export const ServerInfoSchema = z.object({
  name: z.string(),
  version: z.string(),
  title: z.string().optional(),
  description: z.string().optional(),
  website_url: z.string().optional(),
  icons: z.array(IconSchema).optional(),
});
export type ServerInfo = z.infer<typeof ServerInfoSchema>;

// tools/prompts carry an optional `list_changed` flag (list-changed
// notifications) per the MCP spec; resources adds `subscribe`. Mirrors the
// backend model/mcp Capabilities types. logging/sampling are advertised as
// empty objects — presence is the full signal — so they stay loose.
export const ToolsCapabilitySchema = z.object({
  list_changed: z.boolean().optional(),
});

export const PromptsCapabilitySchema = z.object({
  list_changed: z.boolean().optional(),
});

export const ResourcesCapabilitySchema = z.object({
  subscribe: z.boolean().optional(),
  list_changed: z.boolean().optional(),
});

export const CapabilitiesSchema = z.object({
  tools: ToolsCapabilitySchema.passthrough().optional(),
  resources: ResourcesCapabilitySchema.optional(),
  prompts: PromptsCapabilitySchema.passthrough().optional(),
  logging: z.object({}).passthrough().optional(),
  sampling: z.object({}).passthrough().optional(),
});

export const MCPConfigSchema = z.object({
  // Loose by design: read-path data can be a half-created / not-yet-discovered
  // MCP (e.g. transport:"" until discovery runs). `.catch` coerces any empty/
  // unknown value to the default instead of throwing — one such row must never
  // reject the whole list (z.array throws on a single bad element).
  transport: z.union([TransportSchema, z.literal("auto")]).catch("streamable_http"),
  endpoint_url: z.string().optional(),
  // stdio reserved fields (Phase 1: frontend hidden but schema permits)
  command: z.string().optional(),
  args: z.array(z.string()).optional(),
  env: z.record(z.string(), z.string()).optional(),
  sandbox_path: z.string().optional(),
  custom_headers: z.array(CustomHeaderSchema).optional(),
  // Discovery cache (read-only fields populated server-side)
  detected_transport: z.string().optional(),
  last_discovered_at: z.string().optional(),
  tool_count: z.number().int().optional(),
  resource_count: z.number().int().optional(),
  prompt_count: z.number().int().optional(),
  protocol_version: z.string().optional(),
  instructions: z.string().optional(),
  server_info: ServerInfoSchema.optional(),
  capabilities: CapabilitiesSchema.optional(),
  // Tool policy
  tool_allowlist: z.array(z.string()).optional(),
  tool_denylist: z.array(z.string()).optional(),
});
export type MCPConfig = z.infer<typeof MCPConfigSchema>;

export const ScopeLevelSchema = z.enum(["user", "workspace", "organization"]);
export type ScopeLevel = z.infer<typeof ScopeLevelSchema>;

// Lightweight capability summary that ships back on SystemRecord rows. The full
// catalog (tools/resources/prompts) lives in the MCPCapabilities collection and
// is fetched on demand via /v1/mcp/discover.
export const MCPCapabilitiesSummarySchema = z.object({
  tool_count: z.number().int().optional(),
  resource_count: z.number().int().optional(),
  prompt_count: z.number().int().optional(),
  server_info: ServerInfoSchema.optional(),
  last_discovered_at: z.string().optional(),
});
export type MCPCapabilitiesSummary = z.infer<typeof MCPCapabilitiesSummarySchema>;
